// src/components/manager-v2/TeamManagement.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Mail, Phone, Calendar, MapPin, Briefcase, Filter, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useEmployees } from '../../contexts/EmployeeContext';

const TeamManagement = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { employees } = useEmployees();
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredTeam, setFilteredTeam] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');
    const [departmentFilter, setDepartmentFilter] = useState('all');
    const [designationFilter, setDesignationFilter] = useState('all');

    // Get all team members
    const teamMembers = useMemo(() => {
        if (!user || !employees) return [];
        return employees.filter(emp =>
            emp.reporting_manager_id === user.id || emp.managerId === user.id
        );
    }, [user, employees]);

    // Get unique departments and designations for filters
    const departments = useMemo(() => {
        const uniqueDepts = [...new Set(teamMembers.map(e => e.department).filter(Boolean))];
        return uniqueDepts.sort();
    }, [teamMembers]);

    const designations = useMemo(() => {
        const uniqueDesigs = [...new Set(teamMembers.map(e => e.designation).filter(Boolean))];
        return uniqueDesigs.sort();
    }, [teamMembers]);

    // Apply all filters
    useEffect(() => {
        let filtered = teamMembers;

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(emp => {
                const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
                const searchLower = searchTerm.toLowerCase();
                return fullName.includes(searchLower) ||
                    emp.email?.toLowerCase().includes(searchLower) ||
                    emp.employee_code?.toLowerCase().includes(searchLower) ||
                    emp.department?.toLowerCase().includes(searchLower) ||
                    emp.designation?.toLowerCase().includes(searchLower);
            });
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(emp => emp.status === statusFilter);
        }

        // Department filter
        if (departmentFilter !== 'all') {
            filtered = filtered.filter(emp => emp.department === departmentFilter);
        }

        // Designation filter
        if (designationFilter !== 'all') {
            filtered = filtered.filter(emp => emp.designation === designationFilter);
        }

        setFilteredTeam(filtered);
    }, [searchTerm, statusFilter, departmentFilter, designationFilter, teamMembers]);

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setDepartmentFilter('all');
        setDesignationFilter('all');
    };

    // Check if any filters are active
    const hasActiveFilters = searchTerm || statusFilter !== 'all' || departmentFilter !== 'all' || designationFilter !== 'all';

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team Management</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Manage your team of {filteredTeam.length} member{filteredTeam.length !== 1 ? 's' : ''}
                    </p>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
                {/* Search Bar */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search by name, email, employee code, department, or designation..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>

                {/* Filter Row */}
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <Filter className="w-4 h-4" />
                        <span className="text-sm font-medium">Filters:</span>
                    </div>

                    {/* Status Filter */}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Status</option>
                        <option value="Active">Active</option>
                        <option value="On Leave">On Leave</option>
                        <option value="Inactive">Inactive</option>
                    </select>

                    {/* Department Filter */}
                    <select
                        value={departmentFilter}
                        onChange={(e) => setDepartmentFilter(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        disabled={departments.length === 0}
                    >
                        <option value="all">All Departments</option>
                        {departments.map(dept => (
                            <option key={dept} value={dept}>{dept}</option>
                        ))}
                    </select>

                    {/* Designation Filter */}
                    <select
                        value={designationFilter}
                        onChange={(e) => setDesignationFilter(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        disabled={designations.length === 0}
                    >
                        <option value="all">All Designations</option>
                        {designations.map(desig => (
                            <option key={desig} value={desig}>{desig}</option>
                        ))}
                    </select>

                    {/* Clear Filters Button */}
                    {hasActiveFilters && (
                        <button
                            onClick={clearFilters}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4" />
                            Clear Filters
                        </button>
                    )}

                    {/* Results Count */}
                    <div className="ml-auto text-sm text-gray-600 dark:text-gray-400">
                        Showing {filteredTeam.length} of {teamMembers.length} team members
                    </div>
                </div>
            </div>

            {/* Team Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Total Team</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{filteredTeam.length}</p>
                        </div>
                        <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                </div>

                

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Departments</p>
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                {new Set(filteredTeam.map(e => e.department)).size}
                            </p>
                        </div>
                        <Briefcase className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                    </div>
                </div>
            </div>

            {/* Team Members Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTeam.map((employee) => (
                    <div
                        key={employee.id}
                        onClick={() => navigate(`/manager/team-management/${employee.id}`)}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer p-6"
                    >
                        {/* Employee Avatar */}
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
                                {employee.first_name?.[0]}{employee.last_name?.[0]}
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {employee.first_name} {employee.last_name}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{employee.designation}</p>
                            </div>
                        </div>

                        {/* Employee Details */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <Mail className="w-4 h-4" />
                                <span className="truncate">{employee.email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <Phone className="w-4 h-4" />
                                <span>{employee.phone || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <Briefcase className="w-4 h-4" />
                                <span>{employee.department}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <MapPin className="w-4 h-4" />
                                <span>{employee.location || 'Not specified'}</span>
                            </div>
                        </div>

                        {/* Status Badge */}
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                                employee.status === 'Active'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : employee.status === 'On Leave'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                                {employee.status || 'Active'}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Empty State */}
            {filteredTeam.length === 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                    <Users className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Team Members</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                        {searchTerm ? 'No team members match your search.' : 'You don\'t have any team members assigned yet.'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default TeamManagement;
